import React, { useEffect, useState } from "react";
import { Bar, Pie } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from "chart.js";
import { api } from "../../utils/api";
import ChartDataLabels from "chartjs-plugin-datalabels";

// Register the required components from Chart.js
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, ChartDataLabels);

export const fetchActiveSubscriptionCount = async (appliedFilter = {}) => {
  const payload = {};
  const productIds = appliedFilter?.product?.$in;
  if (Array.isArray(productIds) && productIds.length > 0) {
    payload.productIds = productIds;
  }

  try {
    const response = await api("v1/subscription/getAllActiveSubscriptions", "POST", payload);
    if (response.data?.success) {
      return response.data.data?.activeSubscriptionsCount ?? 0;
    }
    return 0;
  } catch (error) {
    console.error("Error in fetchActiveSubscriptionCount:", error);
    return 0;
  }
};

export const fetchRedemptionData = async (dataSource, appliedFilter) => {
  const endpoint = dataSource === "redemptions" ? "v1/redemption/search" : "v1/transaction/search";

  // Add selectedPortal to the request
  const selectedPortal = sessionStorage.getItem("selectedPortal");

  // Ensure appliedFilter is a valid object and add default values
  const safeFilter = appliedFilter && typeof appliedFilter === "object" ? appliedFilter : {};

  const requestBody = {
    query: safeFilter,
    selectedPortal: selectedPortal,
    requestSource: "dashboard", // Indicate this is a dashboard request
    limit: 10000 // Set a very high limit to get all results for charts
  };

  try {
    const response = await api(endpoint, "POST", requestBody);

    // Handle different response structures
    if (response.data && response.data.data && response.data.data.searchResult) {
      return response.data.data.searchResult;
    } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
      return response.data.data;
    } else if (response.data && Array.isArray(response.data)) {
      return response.data;
    } else {
      console.error("Unexpected response structure:", response);
      return [];
    }
  } catch (error) {
    console.error("Error in fetchRedemptionData:", error);
    return [];
  }
};

export const countRedemptionsByMerchantAccount = (redemptionData) => {
  const redemptionByMerchantAccount = {};
  redemptionData.forEach((sale) => {
    const merchantName = sale.merchantStore?.merchantAccount?.name || "Unknown";
    if (!redemptionByMerchantAccount[merchantName]) {
      redemptionByMerchantAccount[merchantName] = { count: 0, totalPrice: 0 };
    }
    redemptionByMerchantAccount[merchantName].count += 1;
    const value = sale.totalValue != null ? parseFloat(sale.totalValue) : (parseFloat(sale.price) || 0);
    redemptionByMerchantAccount[merchantName].totalPrice += value;
  });
  return redemptionByMerchantAccount;
};

export const countRedemptionsByMerchantStore = (redemptionData, merchantAccount = null) => {
  const redemptionByMerchantStore = {};
  redemptionData.forEach((sale) => {
    if (!merchantAccount || sale.merchantStore?.merchantAccount?.name === merchantAccount) {
      const storeName = sale.merchantStore?.name || "Unknown";
      if (!redemptionByMerchantStore[storeName]) {
        redemptionByMerchantStore[storeName] = { count: 0, totalPrice: 0 };
      }
      redemptionByMerchantStore[storeName].count += 1;
      const value = sale.totalValue != null ? parseFloat(sale.totalValue) : (parseFloat(sale.price) || 0);
      redemptionByMerchantStore[storeName].totalPrice += value;
    }
  });
  return redemptionByMerchantStore;
};

export const countRedemptionsByProduct = (redemptionData, merchantStore) => {
  const redemptionByProduct = {};
  redemptionData.forEach((sale) => {
    if (sale.merchantStore?.name === merchantStore) {
      const productName = sale.product?.name || "Unknown";
      if (!redemptionByProduct[productName]) {
        redemptionByProduct[productName] = { count: 0, totalPrice: 0 };
      }
      redemptionByProduct[productName].count += 1;
      const value = sale.totalValue != null ? parseFloat(sale.totalValue) : (parseFloat(sale.price) || 0);
      redemptionByProduct[productName].totalPrice += value;
    }
  });
  return redemptionByProduct;
};
